import React from "react";
import { Link } from "react-router-dom";
import MainMenu from "@/components/MainMenu";
import SeoHead from "@/components/SeoHead";

export default function WhyResumeNoInterviewsPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      <SeoHead
        title="Why Your Resume Is Not Getting Interviews | JOBFITAI"
        description="See the most common technical and targeting reasons resumes miss interviews, and learn how to improve resume alignment."
        canonicalPath="/why-your-resume-is-not-getting-interviews"
        robots="index,follow"
      />
      <div className="max-w-5xl mx-auto px-6 py-16 md:py-20">
        <div className="mb-8 flex items-center gap-3">
          <MainMenu tone="dark" />
          <Link
            to="/resources"
            className="text-blue-400 hover:text-blue-300 transition-colors"
          >
            ← Back to Resources
          </Link>
        </div>

        <article className="bg-slate-900/70 backdrop-blur-sm border border-slate-800 rounded-3xl p-8 md:p-12 shadow-2xl">
          <header className="mb-10">
            <p className="text-sm uppercase tracking-[0.2em] text-blue-400 font-semibold mb-3">
              Resume Tips
            </p>
            <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-4">
              Why Your Resume Is Not Getting Interviews
            </h1>
            <p className="text-slate-300 text-lg leading-8">
              If you have been applying to jobs and not hearing back, you are
              not alone. More often than not, the issue is not your background.
              It is how your resume is presenting it.
            </p>
          </header>

          <div className="prose prose-invert prose-slate max-w-none">
            <p>
              A resume is supposed to earn you an interview by showing how your
              qualifications, experience, and education match a particular
              role. When that match is unclear, weak, or buried in vague
              language, even strong candidates can get overlooked.
            </p>

            <p>
              JOBFITAI helps you identify where your resume may be falling short
              so you can strengthen it before your next application.
            </p>

            <div className="my-8">
              <Link
                to="/pricing"
                className="inline-flex items-center rounded-xl bg-blue-600 px-5 py-3 text-white font-semibold hover:bg-blue-500 transition-colors"
              >
                Analyze My Resume
              </Link>
            </div>

            <h2>1. Your resume is too generic</h2>
            <p>
              One of the most common reasons resumes get ignored is that they
              sound broad rather than targeted.
            </p>
            <p>
              If your summary, skills section, and work history could apply to
              almost any job in any industry, the employer may not see a
              compelling reason to call you. Generic language like
              &quot;hard working professional,&quot; &quot;team player,&quot; or
              &quot;strong communication skills&quot; does not say much on its
              own.
            </p>

            <h2>2. Your resume is not matched to the job posting</h2>
            <p>
              A well targeted resume should mirror the language, priorities, and
              qualifications in the job posting.
            </p>
            <p>
              If the employer emphasizes specific tools, skills, certifications,
              or responsibilities and your resume barely mentions them, the fit
              can be easy to miss. This is especially true when employers use
              applicant tracking systems to sort and score applications.
            </p>

            <h2>3. Your resume does not clearly prove you are qualified</h2>
            <p>
              Resumes often fail not because the candidate is underqualified but
              because the document does not prove they are qualified for that
              specific role.
            </p>
            <p>
              Your resume needs to show evidence through relevant skills,
              experience, accomplishments, training, and education.
            </p>
            <p>
              If a hiring manager cannot quickly answer these three questions
              after reading your resume, you may be losing opportunities:
            </p>
            <ul>
              <li>What role is this person targeting?</li>
              <li>Why are they qualified?</li>
              <li>How have they demonstrated those qualifications?</li>
            </ul>

            <h2>4. Your bullet points are too vague</h2>
            <p>
              Weak bullet points make it genuinely difficult for employers to
              understand what you actually did or what value you brought.
            </p>
            <p>
              <strong>Weak examples:</strong> Helped customers with issues.
              Worked on projects. Assisted with training. Responsible for data
              entry.
            </p>
            <p>
              These do not give the reader much to go on. Stronger bullet points
              start with a clear action verb, describe relevant responsibilities
              or accomplishments, stay concise, and show results or scope where
              possible.
            </p>
            <p>
              <strong>Weak:</strong> Helped with onboarding.
            </p>
            <p>
              <strong>Stronger:</strong> Supported onboarding by training new
              hires on workflow procedures, documentation standards, and daily
              process expectations.
            </p>

            <h2>5. Your resume focuses on duties instead of accomplishments</h2>
            <p>
              Employers want more than a list of tasks you were assigned. They
              want to see evidence of the value you actually delivered.
            </p>
            <p>
              Think about things like improved efficiency, increased accuracy,
              reduced errors, stronger documentation practices, higher
              productivity, cost savings, or safety improvements.
            </p>

            <h2>6. Your keywords are weak or missing</h2>
            <p>
              Keywords help both employers and applicant tracking systems
              recognize how closely your background matches what they are
              looking for.
            </p>
            <p>
              Important resume keywords typically include job titles, technical
              skills, software and systems, credentials, certifications,
              industry terminology, and specific requirements from the posting.
              If those terms are absent from your resume, it may not read as
              relevant as your actual experience warrants.
            </p>

            <h2>7. Your summary is too vague</h2>
            <p>
              A summary should be short, focused, and directly tied to the role
              you are targeting.
            </p>
            <p>
              Weak summaries tend to be overly general, a little fluffy, or more
              about how you see yourself than about what you offer the employer.
            </p>

            <h2>8. Your formatting makes the resume harder to read</h2>
            <p>
              A resume needs to work well for both people and software, and
              complex formatting can create problems on both fronts.
            </p>
            <p>
              Common culprits include too many design elements, tables,
              graphics, images, headers and footers, fancy fonts, borders,
              shading, and cluttered layouts. Simple, clean formatting is almost
              always stronger than visually impressive formatting.
            </p>

            <h2>9. Your resume has spelling or grammar errors</h2>
            <p>
              Proofreading still matters. Even a highly qualified candidate can
              appear less professional when the document contains avoidable
              mistakes.
            </p>

            <h2>10. Your strongest experience is buried</h2>
            <p>
              Your best evidence of fit should not be hard to find.
            </p>
            <p>
              If your most relevant achievements are tucked under weaker bullets
              or pushed toward the bottom of your experience section, an
              employer may never reach them.
            </p>

            <h2>Quick self check</h2>
            <p>Your resume may need work if:</p>
            <ul>
              <li>You are using the same version for every application</li>
              <li>Your summary is broad or unclear</li>
              <li>Your bullets sound generic</li>
              <li>Your keywords do not reflect the posting</li>
              <li>Your experience section focuses only on duties</li>
              <li>Your formatting is overly complex</li>
              <li>Your strongest evidence is buried</li>
              <li>There are spelling or grammar mistakes anywhere in the document</li>
            </ul>

            <h2>What to fix first</h2>
            <p>Start here and work your way through:</p>
            <ul>
              <li>Match the resume to the specific job target</li>
              <li>Strengthen your summary</li>
              <li>Rewrite the weakest bullet points</li>
              <li>Add any missing relevant keywords naturally</li>
              <li>Bring accomplishments and measurable results forward</li>
              <li>Simplify your formatting</li>
              <li>Proofread carefully</li>
            </ul>

            <p>
              You do not need a perfect resume. You need a clearer, more
              targeted one.
            </p>

            <h2>How JOBFITAI helps</h2>
            <p>
              JOBFITAI helps you figure out why your resume may be
              underperforming by showing you where it looks too generic, where
              your qualifications are not coming through clearly enough, which
              keywords may be absent, which sections need better alignment, and
              what to improve before you apply again.
            </p>

            <p>
              That gives you something concrete to act on rather than just
              guessing.
            </p>

            <div className="my-8">
              <Link
                to="/pricing"
                className="inline-flex items-center rounded-xl bg-blue-600 px-5 py-3 text-white font-semibold hover:bg-blue-500 transition-colors"
              >
                Analyze Resume Now
              </Link>
            </div>

            <h2>FAQ</h2>

            <h3>Can a qualified person still get ignored because of a weak resume?</h3>
            <p>
              Absolutely. A resume can fall short even when the candidate is
              well qualified, simply because the document does not make that
              qualification obvious for the target role.
            </p>

            <h3>Are keywords really that important?</h3>
            <p>
              Yes, especially when they reflect real skills, tools,
              credentials, and requirements from the posting.
            </p>

            <h3>Is ATS always the problem?</h3>
            <p>
              Not necessarily. Sometimes the real issue is weak targeting, vague
              bullet points, or a lack of proof. ATS is one piece of the puzzle,
              not the whole picture.
            </p>

            <h3>Should I rewrite my whole resume?</h3>
            <p>
              Usually not. Start by improving the most relevant sections and the
              weakest evidence first, then build from there.
            </p>

            <h3>What should I remove from my resume?</h3>
            <p>
              Remove or condense anything that adds clutter without helping
              prove your fit for the target role.
            </p>
          </div>
        </article>
      </div>
    </div>
  );
}
